/**
 * Módulo Revenue — `/revenue`. **Solo Admin.**
 *
 * La pantalla orquesta y no dibuja: cada bloque es un componente de esta carpeta,
 * y todo lo que se muestra sale de `useRevenue` y `useMetrics`. No hay fixtures ni
 * fetch a mano.
 *
 * El orden es el de la lectura, no el del modelo de datos:
 *
 *  1. Triage — qué se puede leer del mes y qué no.
 *  2. El número por el que se abre el panel (MRR, con ARR adentro) y su serie.
 *  3. La serie mensual editable: la pantalla de carga hasta que Stripe escriba.
 *  4. Dónde está el MRR y por qué — el split explicado.
 *  5. Unit economics: cuánta deuda hay, qué destraba qué, y las 11 métricas.
 *  6. Las suscripciones que forman el MRR, con deep link a Stripe.
 *
 * El permiso lo aplica la API (`requireRole('admin')` sobre toda la ruta). Esto de
 * acá evita pedirle a la API un payload que va a rechazar y explica por qué; no es
 * la autoridad.
 */
import {
  deltaBaseOf,
  formatMetricValue,
  formatPeriodo,
  periodoAnterior,
  type Health,
  type MetricDefinition,
  type MetricValue,
} from '@ops/contracts'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ApiError } from '../../lib/api'
import { useMe, useMetrics, useRevenue } from '../../lib/queries'
import { usePeriodo, useToast } from '../../shell'
import {
  Button,
  Card,
  HeroKpi,
  Section,
  Stack,
  TriageBar,
  type TriageConteo,
} from '../../ui'
import { CargarDatoDialog, Cargando, ErrorDeCarga } from '../comunes'
import { CadenaDeDependencias } from './CadenaDeDependencias'
import { DeudaDeDatos } from './DeudaDeDatos'
import { SplitPorSegmento } from './SplitPorSegmento'
import { SuscripcionesStripe } from './SuscripcionesStripe'
import { TablaMensual } from './TablaMensual'
import { TablaUnitEconomics } from './TablaUnitEconomics'
import { TendenciaMrr } from './TendenciaMrr'
import { cadenasDe, deudaDe, filasDe, palancaDe } from './dependencias'
import { SinAcceso } from './SinAcceso'

export interface RevenuePageProps {
  /** Reloj de la pantalla. Inyectable para tests y para que la frescura de todos
   *  los badges sea la misma en un render. */
  now?: Date
}

/**
 * Puerta de rol. Resuelve `/api/me` (que el shell ya cacheó) antes de montar la
 * pantalla, así un rol sin permiso no dispara el request que la API va a rechazar.
 */
export default function RevenuePage({ now }: RevenuePageProps = {}) {
  const { data: me, isLoading, error } = useMe()

  if (isLoading) return <Cargando que="tu acceso" />
  if (error !== null || me === undefined) {
    return (
      <SinAcceso
        {...(error instanceof ApiError ? { mensaje: error.message } : {})}
      />
    )
  }
  if (me.rol !== 'admin') return <SinAcceso rol={me.rol} />

  return <RevenueAdmin {...(now === undefined ? {} : { now })} />
}

function RevenueAdmin({ now: nowProp }: { now?: Date }) {
  const { periodo } = usePeriodo()
  const toast = useToast()
  const navegar = useNavigate()
  const [ahoraLocal] = useState(() => new Date())
  const ahora = nowProp ?? ahoraLocal
  const [slugCarga, setSlugCarga] = useState<string | null>(null)

  const revenue = useRevenue(periodo)
  const metrics = useMetrics(periodo)
  const { data: me } = useMe()

  const definiciones: readonly MetricDefinition[] = metrics.data?.metricas ?? []
  const valores: readonly MetricValue[] = metrics.data?.valores ?? []

  /** Las 11 filas del panel: 9 de unit economics + ARR y NPS del catálogo. */
  const filas = useMemo(() => {
    const deUnitEconomics = revenue.data?.unitEconomics.metricas ?? []
    const porSlug = new Map<string, MetricValue>()
    for (const valor of valores) porSlug.set(valor.slug, valor)
    // Los valores de unit economics tienen prioridad: son los que la API sirvió
    // para esta pantalla.
    for (const valor of deUnitEconomics) porSlug.set(valor.slug, valor)
    return filasDe([...porSlug.values()], definiciones)
  }, [revenue.data, valores, definiciones])

  const deuda = useMemo(() => deudaDe(filas), [filas])
  const cadenas = useMemo(() => cadenasDe(filas), [filas])
  const palanca = useMemo(() => palancaDe(filas, cadenas), [filas, cadenas])
  const filaDeCarga = filas.find((fila) => fila.slug === slugCarga) ?? null

  // Un 403 acá no debería pasar (la puerta de rol ya filtró), pero si la API
  // cambia de opinión gana la API y se muestra su mensaje.
  if (revenue.error instanceof ApiError && revenue.error.esPermiso) {
    return <SinAcceso mensaje={revenue.error.message} />
  }

  if (revenue.isLoading) {
    return <Cargando que={`el revenue de ${formatPeriodo(periodo)}`} />
  }

  if (revenue.isError || revenue.data === undefined) {
    return (
      <ErrorDeCarga
        que={`el revenue de ${formatPeriodo(periodo)}`}
        error={revenue.error}
        onRetry={() => void revenue.refetch()}
        reintentando={revenue.isFetching}
      />
    )
  }

  const { meses, porSegmento, unitEconomics, suscripciones, links } = revenue.data
  const mesActual = meses.find((mes) => mes.periodo === periodo) ?? meses.at(-1)
  const mrr = valores.find((valor) => valor.slug === 'mrr') ?? null
  const arr = valores.find((valor) => valor.slug === 'arr') ?? null
  const definicionDe = (slug: string) =>
    definiciones.find((definicion) => definicion.slug === slug) ?? null

  const conteos: TriageConteo[] = [
    {
      severidad: 'unknown',
      cantidad: deuda.sinDato,
      label: deuda.sinDato === 1 ? 'métrica sin dato' : 'métricas sin dato',
    },
    {
      severidad: 'critical',
      cantidad: deuda.enRojo,
      label: 'fuera de umbral',
    },
    { severidad: 'warning', cantidad: deuda.enAtencion, label: 'en atención' },
    {
      severidad: 'warning',
      cantidad: deuda.vencidas,
      label: deuda.vencidas === 1 ? 'carga vencida' : 'cargas vencidas',
    },
    {
      severidad: 'ok',
      cantidad: deuda.conDato,
      label: 'con dato del período',
    },
  ]

  const estado: Health =
    deuda.enRojo > 0
      ? 'critical'
      : deuda.enAtencion > 0 || deuda.vencidas > 0
        ? 'warning'
        : deuda.sinDato > 0
          ? 'unknown'
          : 'ok'

  const titular =
    deuda.total === 0
      ? 'Sin métricas de unit economics'
      : deuda.sinDato === 0
        ? `Las ${deuda.total} métricas se pueden leer`
        : `${deuda.sinDato} de ${deuda.total} métricas no se pueden leer`

  return (
    <>
      <div className="mb-5 overflow-hidden rounded-card border border-border">
        <TriageBar
          estado={estado}
          titular={titular}
          conteos={conteos}
          meta={`${formatPeriodo(periodo)} · datos según proveniencia de cada fila`}
          acciones={
            palanca.slugAccion === null ? undefined : (
              <Button
                variante="primario"
                icono="lapiz"
                onClick={() => setSlugCarga(palanca.slugAccion)}
              >
                Cargar los datos que faltan ({deuda.sinDato})
              </Button>
            )
          }
        />
      </div>

      <Section
        titulo="Cómo viene el mes"
        bajada={
          mrr === null ? (
            <>
              La pantalla todavía no puede decir en cuánto está el MRR: el
              catálogo de métricas no trajo el KPI <b>mrr</b>. La serie mensual de
              abajo no depende de eso.
            </>
          ) : mrr.valor === null ? (
            <>
              No hay MRR cargado para <b>{formatPeriodo(periodo)}</b>. Sin ese
              número el resto de la pantalla no tiene lectura: cargalo en la fila
              del período de la serie mensual.
            </>
          ) : (
            <>
              El MRR de <b>{formatPeriodo(periodo)}</b> es{' '}
              <b>{formatMetricValue(mrr.valor, 'usd')}</b>, y de las{' '}
              <b>{deuda.total}</b> métricas de unit economics hay{' '}
              <b>{deuda.sinDato}</b> que todavía no se pueden leer.
            </>
          )
        }
      >
        {metrics.isLoading ? (
          <Cargando que="el catálogo de métricas" />
        ) : metrics.isError ? (
          <ErrorDeCarga
            que="el catálogo de métricas"
            error={metrics.error}
            onRetry={() => void metrics.refetch()}
            reintentando={metrics.isFetching}
          />
        ) : mrr === null ? (
          // La API contestó bien y no hay KPI de MRR: es un catálogo incompleto,
          // no un error de red. Decirlo como error mandaría a mirar el lugar
          // equivocado.
          <Card padding="md" hundida punteada>
            <p className="text-meta text-muted-strong">
              El catálogo no tiene la métrica <b className="text-text">mrr</b>{' '}
              para este período. Se da de alta en Admin → catálogo de métricas; es
              un registro, no un deploy.
            </p>
          </Card>
        ) : (
          <HeroKpi
            metric={mrr}
            secundaria={arr}
            base={deltaBaseOf(mrr, periodoAnterior(mrr.periodo) ?? mrr.periodo)}
            direccion={definicionDe('mrr')?.direccion ?? 'higher_better'}
            now={ahora}
            aside={<TendenciaMrr meses={meses} />}
          />
        )}
      </Section>

      <Section
        titulo="La serie mensual es la pantalla de carga"
        bajada={
          <>
            Una fila por mes, editable en línea. Hasta que Stripe escriba por la
            ingesta, este es el lugar donde se corrige el MRR — y toda edición
            queda como <b>carga manual</b>, con nombre y fecha.
          </>
        }
      >
        <TablaMensual
          meses={meses}
          periodoActual={periodo}
          autor={me?.nombre ?? null}
          now={ahora}
          onGuardado={(cantidad) =>
            toast.push(
              'ok',
              cantidad === 1
                ? 'Se guardó 1 mes y se recalculó el growth'
                : `Se guardaron ${cantidad} meses y se recalculó el growth`,
            )
          }
        />
      </Section>

      <Section
        titulo="La concentración es una decisión, no un accidente"
        bajada={
          <>
            El split por segmento no es solo dónde está la plata: es el resultado
            de a quién se le vende. Abajo, con qué cifras se sostiene esa lectura.
          </>
        }
      >
        <SplitPorSegmento
          porSegmento={porSegmento}
          mesActual={mesActual}
          periodo={periodo}
          now={ahora}
        />
      </Section>

      <Section
        titulo={
          deuda.sinDato === 0
            ? 'Unit economics: las 11 se pueden leer'
            : `Unit economics: ${deuda.sinDato} de las ${deuda.total} no se pueden leer`
        }
        bajada={
          <>
            Cada métrica con su fórmula y su benchmark a la vista. Las que faltan
            no muestran un guion: dicen <b>por qué</b> faltan y qué hay que cargar
            primero.
          </>
        }
      >
        {metrics.isLoading ? (
          <Cargando que="el catálogo de métricas" />
        ) : metrics.isError ? (
          <ErrorDeCarga
            que="el catálogo de métricas"
            error={metrics.error}
            onRetry={() => void metrics.refetch()}
            reintentando={metrics.isFetching}
          />
        ) : (
          <Stack gap="md">
            <DeudaDeDatos filas={filas} deuda={deuda} />
            <CadenaDeDependencias
              cadenas={cadenas}
              palanca={palanca}
              bases={unitEconomics.bases}
              onCargar={setSlugCarga}
            />
            <TablaUnitEconomics
              filas={filas}
              now={ahora}
              onCargar={setSlugCarga}
            />
          </Stack>
        )}
      </Section>

      <Section
        id="revenue-suscripciones"
        titulo="De qué suscripciones sale este MRR"
        bajada={
          <>
            El MRR de <b>{formatPeriodo(periodo)}</b> es la suma de estas
            suscripciones. Cada una abre su registro exacto en Stripe; las que no
            tienen registro se marcan en vez de linkear a ninguna parte.
          </>
        }
        espaciado="sm"
      >
        <SuscripcionesStripe
          suscripciones={suscripciones}
          links={links}
          periodo={periodo}
          // El MRR de estas filas y el de la serie mensual son la misma carga
          // de Stripe: se publica su proveniencia y no se inventa otra.
          provenance={mesActual?.provenance ?? null}
          now={ahora}
          onVincular={(nombre) => {
            // Los ids externos se administran en Admin: la acción lleva ahí en vez
            // de abrir un formulario que hoy no tiene endpoint donde escribir.
            toast.push(
              'ok',
              `Vinculá el id de Stripe de ${nombre} en Admin → fuentes de datos`,
            )
            navegar('/admin')
          }}
        />
      </Section>

      {filaDeCarga !== null ? (
        <CargarDatoDialog
          key={filaDeCarga.slug}
          open
          onClose={() => setSlugCarga(null)}
          slug={filaDeCarga.slug}
          nombre={filaDeCarga.nombre}
          periodo={periodo}
          unidad={filaDeCarga.metric.unidad}
          // Cargar un número sin ver su definición es cómo se llenó de cifras
          // inconsistentes el sheet original: la ficha va arriba del campo.
          ficha={[
            {
              rotulo: 'Fórmula',
              valor: filaDeCarga.metric.formula ?? 'Sin fórmula declarada',
            },
            {
              rotulo: 'Benchmark',
              valor: filaDeCarga.metric.benchmark ?? 'Sin benchmark definido',
            },
            {
              rotulo: 'Definición',
              valor:
                filaDeCarga.definicion?.definicion ??
                'Sin definición en el catálogo',
            },
            {
              rotulo: 'Hoy',
              valor: filaDeCarga.conDato
                ? `${formatMetricValue(filaDeCarga.metric.valor, filaDeCarga.metric.unidad)} — se sobrescribe con lo que cargues`
                : (filaDeCarga.motivo ?? 'Sin dato'),
            },
          ]}
        />
      ) : null}
    </>
  )
}
